/**
 * Otomatik Mail Raporlari - Tip Tanimlari
 *
 * Backend referansi: gts_dotnetapi/GTS.Business/DTOs/Reports/*
 * Tum API yanitlari PascalCase, frontend'de de o sekilde sakliyoruz
 * (camelCase'e cevirmek yerine, CRITICAL_API_DEVELOPMENT_RULES uyumu icin).
 */

export type ReportType = 'daily' | 'weekly' | 'monthly';

/**
 * Rapor icerik tipi. Suanda sadece "endOfDay" destekleniyor;
 * yeni IReportProvider eklendiginde bu union genisleyecek.
 */
export type ReportKind = 'endOfDay'; // | 'stockSnapshot' | 'personnelSummary'

export interface ReportSchedule {
  Id: number;
  CompanyId: string;
  BranchIds: string[];
  ReportType: ReportType;
  ReportKind: ReportKind;
  CronExpression: string;
  TimeZoneId: string;
  Recipients: string[];
  CcRecipients: string[];
  Name: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  LastRunAt: string | null;
  NextRunAt: string | null;
  CreatedByEmail: string | null;
  UpdatedByEmail: string | null;
}

export interface ReportScheduleUpsertRequest {
  CompanyId: string;
  BranchIds: string[];
  ReportType: ReportType;
  ReportKind: ReportKind;
  CronExpression: string;
  TimeZoneId: string;
  Recipients: string[];
  CcRecipients?: string[];
  Name?: string | null;
  IsActive: boolean;
}

/**
 * Mevcut + ileride eklenmesi planlanan rapor turleri.
 * Frontend dropdown'unda gosterilen liste — `available: false` olanlar
 * disabled gozukur ("Yakinda" rozeti ile).
 */
export interface ReportKindOption {
  kind: string;
  label: string;
  description: string;
  available: boolean;
}

export const REPORT_KIND_OPTIONS: ReportKindOption[] = [
  {
    kind: 'endOfDay',
    label: 'Gün Sonu Raporu',
    description: 'Günlük satış özeti, ödeme dağılımı, şube kırılımı',
    available: true,
  },
  {
    kind: 'stockSnapshot',
    label: 'Stok Anlık Durum',
    description: 'Belirli zamandaki stok mevcudu, kritik seviye uyarıları',
    available: false,
  },
  {
    kind: 'personnelSummary',
    label: 'Personel Giriş/Çıkış',
    description: 'Dönem bazlı personel mesai ve devamsızlık özeti',
    available: false,
  },
];

export interface ReportRunLog {
  Id: number;
  ScheduleId: number | null;
  CompanyId: string;
  BranchId: string | null;
  ReportType: ReportType;
  Status: 'success' | 'failed' | 'skipped';
  RecipientCount: number;
  TotalRevenue: number | null;
  OrderCount: number | null;
  ErrorMessage: string | null;
  Subject: string | null;
  ExecutedAt: string;
  DurationMs: number;
  TriggerSource: 'schedule' | 'test-send' | 'manual';

  // Migration 004 — Teslim takibi (delivery tracking)
  Recipients?: string[];
  CcRecipients?: string[];
  SmtpHost?: string | null;
  SmtpResponse?: string | null;
  MessageId?: string | null;
}

/**
 * Server-side pagination zarfı.
 * Backend: PagedResult<T> { Items, TotalCount, Page, PageSize }
 */
export interface PagedResult<T> {
  Items: T[];
  TotalCount: number;
  Page: number;
  PageSize: number;
}

export interface CompanySmtp {
  Id: number;
  CompanyId: string | null;
  Host: string;
  Port: number;
  UseSsl: boolean;
  Username: string;
  FromEmail: string;
  FromName: string;
  IsActive: boolean;
  HasPassword: boolean;
  CreatedAt: string;
  UpdatedAt: string;
  UpdatedByEmail: string | null;
}

export interface CompanySmtpUpsertRequest {
  CompanyId: string | null;
  Host: string;
  Port: number;
  UseSsl: boolean;
  Username: string;
  Password?: string | null;
  FromEmail: string;
  FromName: string;
  IsActive: boolean;
}

export interface ReportTemplateSettings {
  Id: number;
  CompanyId: string | null;
  BrandName: string;
  PrimaryColor: string;
  PrimaryDarkColor: string;
  AccentColor: string;
  LogoUrl: string;
  FooterText: string;
  FullHtmlOverride: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  UpdatedByEmail: string | null;
}

export interface ReportTemplateSettingsUpsertRequest {
  CompanyId: string | null;
  BrandName: string;
  PrimaryColor: string;
  PrimaryDarkColor: string;
  AccentColor: string;
  LogoUrl: string;
  FooterText: string;
  FullHtmlOverride?: string | null;
}

export interface TestSendRequest {
  To: string[];
  Cc?: string[];
  /** Tek sube modu (eski uyumluluk). Tercihen BranchIds kullanilmali. */
  BranchId?: string | null;
  BranchName?: string | null;
  /** Coklu sube — her biri icin ayri mail gonderilir (schedule cron davranisi). */
  BranchIds?: string[] | null;
  ReportType?: ReportType;
  StartDate?: string | null;
  EndDate?: string | null;
  /** Schedule'in cron ifadesi. Verilirse, mail icerigi gercek cron periyoduna gore hesaplanir. */
  CronExpression?: string | null;
  /** Schedule'in timezone'u. Default: "Turkey Standard Time". */
  TimeZoneId?: string | null;
}

export interface ApiResponse<T> {
  Success: boolean;
  Data?: T;
  Message?: string;
  Count?: number;
}

/**
 * SuperAdmin - sirket genel bakis bilgisi.
 * Backend: SuperAdminMailReportsController.ListCompanies
 */
export interface SuperAdminCompanySummary {
  CompanyId: string;
  Name: string;
  MailReportsEnabled: boolean;
  HasOwnSmtp: boolean;
  HasOwnTemplate: boolean;
  ScheduleCount: number;
  ActiveScheduleCount: number;
  LastRunAt: string | null;
}

export interface AccessStatus {
  CompanyId: string;
  MailReportsEnabled: boolean;
}

/**
 * Etkili (resolved) SMTP — sirket kullanicilarinin gorebilecegi tek sey.
 * Sifre asla donmez; sadece "var/yok" bilgisi var.
 */
export interface EffectiveSmtp {
  IsCompanySpecific: boolean;
  Host: string;
  Port: number;
  UseSsl: boolean;
  FromEmail: string;
  FromName: string;
  HasPassword: boolean;
  IsActive: boolean;
  UpdatedByEmail: string | null;
  UpdatedAt: string;
}

/**
 * Cron preset'leri - UI'da hizli secim icin.
 * Cron formati: "min hour day month dow" (5 alan).
 */
export interface CronPreset {
  id: string;
  label: string;
  description: string;
  cron: string;
  reportType: ReportType;
}

export const CRON_PRESETS: CronPreset[] = [
  { id: 'daily_22', label: 'Her gün saat 22:00', description: 'Günlük rapor', cron: '0 22 * * *', reportType: 'daily' },
  { id: 'daily_23', label: 'Her gün saat 23:00', description: 'Günlük rapor', cron: '0 23 * * *', reportType: 'daily' },
  { id: 'daily_00', label: 'Her gün saat 00:00', description: 'Günlük rapor (gece yarısı)', cron: '0 0 * * *', reportType: 'daily' },
  { id: 'weekly_mon', label: 'Her Pazartesi saat 09:00', description: 'Haftalık rapor', cron: '0 9 * * 1', reportType: 'weekly' },
  { id: 'weekly_sun_22', label: 'Her Pazar saat 22:00', description: 'Haftalık rapor', cron: '0 22 * * 0', reportType: 'weekly' },
  { id: 'monthly_first', label: 'Her ayın 1\'i saat 09:00', description: 'Aylık rapor', cron: '0 9 1 * *', reportType: 'monthly' },
];
